<template>
  <a-modal
      :width="modalWidth"
      :open="open"
      :footer="null"
      class="modal-no-padding survey-passing-modal"
      :wrap-class-name="isMobile ? 'ant-modal-bottom-wrap' : ''"
      @cancel="handleClose"
  >
    <template #title>
      <div class="modal-no-padding__header survey-passing-modal__header">
        <div class="modal-header">Опрос «{{ surveyTitle }}»</div>
      </div>
      <a-divider style="margin: 16px 0" />
    </template>

    <div
        v-if="config"
        class="modal-no-padding__body survey-passing-modal__body"
        :class="{ 'survey-passing-modal__body--complete': phase === 'complete' }"
    >
      <div v-if="phase === 'question'" class="survey-passing-modal__progress">
        <div class="survey-passing-modal__progress-text">
          Вопрос {{ currentQuestionNumber }} / {{ totalQuestions }}
        </div>
        <a-progress
            class="survey-passing-modal__progress-line"
            :percent="progressPercent"
            :show-info="false"
            :stroke-color="'#6BC85D'"
            :trail-color="'#F0F0F0'"
            :size="6"
        />
      </div>

      <SurveyWelcomeStep
          v-if="phase === 'welcome'"
          :greeting="config.welcome.greeting"
          :anonymity-note="config.welcome.anonymityNote"
          :questions-label="questionsLabel"
          :duration="config.duration"
          :valid-until="config.valid_until ?? ''"
      />

      <div
          v-if="phase === 'welcome' && requiresJobLevel"
          class="survey-passing-modal__job-level"
      >
        <label class="survey-passing-modal__job-level-label" for="survey-job-level">
          Выберите уровень должности
        </label>
        <a-select
            id="survey-job-level"
            v-model:value="selectedJobLevel"
            :options="jobLevelOptions"
            placeholder="Выберите уровень должности"
            class="survey-passing-modal__job-level-select"
        />
      </div>

      <SurveyCompleteStep v-else-if="phase === 'complete'" />

      <div v-else-if="phase === 'question'" class="survey-passing-modal__questions">
        <div
            v-for="step in visibleQuestionSteps"
            :key="step.id"
            class="survey-passing-modal__question"
        >
          <SurveyRadioQuestion
              v-if="step.type === 'radio'"
              v-model="answers[step.id]"
              :title="step.title"
              :highlight="step.highlight"
              :group-title="step.groupTitle"
              :options="step.options"
          />

          <SurveyScaleQuestion
              v-else-if="step.type === 'scale'"
              v-model="answers[step.id]"
              :title="step.title"
              :statements="step.statements"
              :min="step.min"
              :max="step.max"
          />

          <SurveyEnergySliderQuestion
              v-else-if="step.type === 'energy'"
              v-model="answers[step.id]"
              :title="step.title"
              :hint="step.hint"
              :field-label="step.fieldLabel"
              :min="step.min"
              :max="step.max"
          />

          <SurveyEmotionQuestion
              v-else-if="step.type === 'emotions'"
              v-model="answers[step.id]"
              :title="step.title"
              :min="step.min"
              :max="step.max"
              :options="step.options"
          />
        </div>
      </div>
    </div>

    <a-divider v-if="config" style="margin: 16px 0" />

    <div v-if="config" class="modal-no-padding__footer survey-passing-modal__footer">
      <template v-if="phase === 'welcome'">
        <a-button
            class="secondary-button"
            :disabled="isDelaying"
            :loading="isDelaying"
            @click="remindLater"
        >
          Напомнить через час
        </a-button>
        <a-button
            class="primary-button"
            :disabled="requiresJobLevel && !selectedJobLevel"
            :loading="isStarting"
            @click="startSurvey"
        >
          {{ startButtonText }}
        </a-button>
      </template>

      <template v-else-if="phase === 'complete'">
        <a-button class="secondary-button" @click="handleClose">Закрыть</a-button>
      </template>

      <template v-else>
        <a-button class="secondary-button" @click="goBack">Назад</a-button>
        <a-button
            class="primary-button"
            :disabled="!canGoNext || isSubmitting"
            :loading="isSubmitting"
            @click="goNext"
        >
          {{ nextButtonText }}
        </a-button>
      </template>
    </div>
  </a-modal>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import { createSurveyPassingConfig, getSurveyPassingConfig } from '@/feature/surveys/constants/surveyPassingConfigs';
import SurveyWelcomeStep from '@/feature/surveys/steps/SurveyWelcomeStep.vue';
import SurveyCompleteStep from '@/feature/surveys/steps/SurveyCompleteStep.vue';
import SurveyRadioQuestion from '@/feature/surveys/questions/SurveyRadioQuestion.vue';
import SurveyScaleQuestion from '@/feature/surveys/questions/SurveyScaleQuestion.vue';
import SurveyEnergySliderQuestion from '@/feature/surveys/questions/SurveyEnergySliderQuestion.vue';
import SurveyEmotionQuestion from '@/feature/surveys/questions/SurveyEmotionQuestion.vue';
import {getWordFormBasedOnTheCount} from "@/views/Report/utils";

const createDefaultAnswer = (step) => {
  if (step.initialValue !== undefined) {
    if (Array.isArray(step.initialValue)) {
      return [...step.initialValue];
    }

    return step.initialValue;
  }

  switch (step.type) {
    case 'emotions':
      return [];
    case 'scale':
      return step.statements.map(() => null);
    case 'radio':
      return null;
    case 'energy':
      return step.max ? Math.round(step.max / 2) : 50;
    default:
      return null;
  }
};

const isStepValid = (step, value) => {
  if (!step) {
    return false;
  }

  switch (step.type) {
    case 'emotions':
      return value.length >= step.min && value.length <= step.max;
    case 'radio':
      return value !== null && value !== undefined;
    case 'scale':
      return Array.isArray(value) && value.length === step.statements.length && value.every((item) => item !== null);
    case 'energy':
      return value !== null && value !== undefined;
    default:
      return true;
  }
};

const hasSurveyProgress = (surveyData) => {
  return surveyData?.questions?.some((el) => el.saved_answers?.length > 0);
};

export default {
  name: 'SurveyPassingModal',
  components: {
    SurveyWelcomeStep,
    SurveyCompleteStep,
    SurveyRadioQuestion,
    SurveyScaleQuestion,
    SurveyEnergySliderQuestion,
    SurveyEmotionQuestion,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    surveyId: {
      type: String,
      default: '',
    },
    surveyData: {
      type: Object,
      default: null,
    },
  },
  emits: ['close', 'completed'],
  setup(props, { emit }) {
    const store = useStore();
    const isMobile = ref(false);
    const phase = ref('welcome');
    const stepIndex = ref(0);
    const isSubmitting = ref(false);
    const isStarting = ref(false);
    const isDelaying = ref(false);
    const answers = reactive({});

    const jobLevels = computed(() => store.state.periodicSurveys.jobLevels ?? []);
    const selectedJobLevel = ref(null);
    const requiresJobLevel = computed(() => {
      return config.value?.period === 'quarterly';
    });
    const jobLevelOptions = computed(() => {
      return jobLevels.value.map((level) => ({
        value: level.id,
        label: level.name,
      }));
    });

    const emotionOptions = computed(() => {
      return (store.state.diary?.emotionList ?? []).map((emotion) => ({
        id: emotion.id,
        text: emotion.value ?? emotion.text ?? emotion.name,
      }));
    });

    const config = computed(() => {
      return createSurveyPassingConfig(props.surveyData, emotionOptions.value) ?? getSurveyPassingConfig(props.surveyId);
    });

    const storageKey = computed(() => {
      if (!config.value?.code) {
        return '';
      }

      return `periodic-survey:${config.value.code}:answers`;
    });

    const surveyTitle = computed(() => config.value?.title ?? '');
    const questionsLabel = computed(() => {
      const count = config.value?.questionsCount ?? 0;

      return `${count} ${getWordFormBasedOnTheCount(count, 'вопрос', 'вопроса', 'вопросов')}`;
    });

    const totalQuestions = computed(() => config.value?.steps?.length ?? 0);

    const currentStep = computed(() => config.value?.steps?.[stepIndex.value] ?? null);

    const visibleQuestionSteps = computed(() => {
      return currentStep.value ? [currentStep.value] : [];
    });

    const currentQuestionNumber = computed(() => stepIndex.value + 1);

    const isLastQuestion = computed(() => currentQuestionNumber.value >= totalQuestions.value);

    const nextButtonText = computed(() => (isLastQuestion.value ? 'Завершить' : 'Далее'));

    const startButtonText = computed(() => (hasSurveyProgress(props.surveyData) ? 'Продолжить' : 'Начать'));

    const progressPercent = computed(() => {
      if (!totalQuestions.value) {
        return 0;
      }

      return Math.round((currentQuestionNumber.value / totalQuestions.value) * 100);
    });

    const modalWidth = computed(() => {
      return 800;
    });

    const canGoNext = computed(() => {
      if (!currentStep.value) {
        return false;
      }

      return visibleQuestionSteps.value.every((step) => isStepValid(step, answers[step.id]));
    });

    const loadStoredAnswers = () => {
      if (!storageKey.value) {
        return {};
      }

      try {
        return JSON.parse(localStorage.getItem(storageKey.value)) ?? {};
      } catch (error) {
        return {};
      }
    };

    const initAnswers = () => {
      Object.keys(answers).forEach((key) => delete answers[key]);
      const storedAnswers = loadStoredAnswers();

      config.value?.steps?.forEach((step) => {
        answers[step.id] = storedAnswers[step.id] !== undefined
            ? storedAnswers[step.id]
            : createDefaultAnswer(step);
      });
    };

    const getFirstIncompleteStepIndex = () => {
      const steps = config.value?.steps ?? [];

      if (!hasSurveyProgress(props.surveyData)) {
        return 0;
      }

      const index = steps.findIndex((step) => !isStepValid(step, answers[step.id]));

      if (index >= 0) {
        return index;
      }

      return Math.max(steps.length - 1, 0);
    };

    const resetFlow = () => {
      phase.value = 'welcome';
      initAnswers();
      stepIndex.value = getFirstIncompleteStepIndex();
    };

    const handleClose = () => {
      emit('close');
      store.dispatch('periodicSurveys/getAvailable').catch(() => null);
    };

    const remindLater = async () => {
      isDelaying.value = true;

      try {
        await store.dispatch('periodicSurveys/createNotification', { time: 60 });
        message.success('Напоминание будет отправлено через час');
        handleClose();
      } catch (error) {
        message.error('Не удалось создать напоминание');
      } finally {
        isDelaying.value = false;
      }
    };

    const buildStatementPayload = (step, value) => {
      return step.statements.reduce((result, statement, index) => {
        result[statement.questionId ?? statement.id] = Number(value[index]);

        return result;
      }, {});
    };

    const buildStepPayload = (step) => {
      const value = answers[step.id];

      switch (step.type) {
        case 'radio':
          return [{
            question_id: step.questionId,
            answer_id: value,
          }];
        case 'energy':
          return [{
            question_id: step.questionId,
            value: Number(value),
          }];
        case 'emotions':
          return [{
            question_id: step.questionId,
            value: value.map((item) => Number(item)),
          }];
        default:
          return [];
      }
    };

    const buildSubmitRequest = () => {
      const request = {
        code: config.value.code,
      };
      const answerSteps = visibleQuestionSteps.value.filter((step) => step.type !== 'scale');
      const statementStep = visibleQuestionSteps.value.find((step) => step.type === 'scale');
      const answersPayload = answerSteps.flatMap((step) => buildStepPayload(step));

      if (answersPayload.length) {
        request.answers = answersPayload;
      }

      if (statementStep) {
        request.statements = buildStatementPayload(statementStep, answers[statementStep.id]);
      }

      return request;
    };

    const clearStoredAnswers = () => {
      if (storageKey.value) {
        localStorage.removeItem(storageKey.value);
      }
    };

    const submitProgress = async () => {
      if (!config.value?.code) {
        return null;
      }

      const payload = buildSubmitRequest();

      if (!payload.answers?.length && !payload.statements) {
        return null;
      }

      isSubmitting.value = true;

      try {
        let result = null;

        if (payload.answers?.length) {
          result = await store.dispatch('periodicSurveys/submit', {
            code: payload.code,
            answers: payload.answers,
          });
        }

        if (payload.statements) {
          result = await store.dispatch('periodicSurveys/submit', {
            code: payload.code,
            statements: payload.statements,
          });
        }

        return result;
      } catch (error) {
        message.error('Ошибка');
        return false;
      } finally {
        isSubmitting.value = false;
      }
    };

    const completeSurvey = async () => {
      clearStoredAnswers();
      await store.dispatch('periodicSurveys/getAvailable').catch(() => null);
      phase.value = 'complete';
      emit('completed', { surveyId: props.surveyId, code: config.value?.code, answers: { ...answers } });
    };

    const startSurvey = async () => {
      if (requiresJobLevel.value) {
        isStarting.value = true;
        try {
          await store.dispatch('periodicSurveys/setPassingJobLevel', {
            survey_passing_id: config.value.code,
            job_level_id: selectedJobLevel.value,
          });
        } finally {
          isStarting.value = false;
        }
      }

      if (!config.value?.steps?.length) {
        phase.value = 'complete';
        return;
      }

      phase.value = 'question';
      stepIndex.value = getFirstIncompleteStepIndex();
    };

    const goBack = () => {
      if (stepIndex.value > 0) {
        stepIndex.value -= 1;
        return;
      }

      phase.value = 'welcome';
    };

    const goNext = async () => {
      if (!canGoNext.value) {
        return;
      }

      const submitResult = await submitProgress();

      if (submitResult === false) {
        return;
      }

      if (submitResult?.finished_at) {
        await completeSurvey();
        return;
      }

      if (stepIndex.value < totalQuestions.value - 1) {
        stepIndex.value += 1;
        return;
      }

      await completeSurvey();
    };

    const updateMobile = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    watch(
        () => [props.open, props.surveyId, props.surveyData?.code],
        ([isOpen]) => {
          if (isOpen && config.value) {
            resetFlow();
            selectedJobLevel.value = null;
            if (requiresJobLevel.value) {
              store.dispatch('periodicSurveys/getJobLevels');
            }
          }
        },
    );

    watch(
        answers,
        () => {
          if (props.open && storageKey.value) {
            localStorage.setItem(storageKey.value, JSON.stringify({ ...answers }));
          }
        },
        { deep: true },
    );

    onMounted(() => {
      updateMobile();
      window.addEventListener('resize', updateMobile);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateMobile);
    });

    return {
      isMobile,
      phase,
      config,
      surveyTitle,
      questionsLabel,
      totalQuestions,
      currentStep,
      visibleQuestionSteps,
      currentQuestionNumber,
      progressPercent,
      nextButtonText,
      startButtonText,
      modalWidth,
      answers,
      requiresJobLevel,
      selectedJobLevel,
      jobLevelOptions,
      canGoNext,
      isSubmitting,
      isStarting,
      isDelaying,
      handleClose,
      remindLater,
      startSurvey,
      goBack,
      goNext,
    };
  },
};
</script>

<style scoped lang="scss">
.survey-passing-modal {
  &__header {
    padding-right: 24px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 120px;
  }

  &__progress {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__job-level {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__job-level-label {
    color: rgba(0, 0, 0, 0.88);
    font-size: 14px;
    font-weight: 600;
    line-height: 120%;
    font-family: "Open Sans", sans-serif;
  }

  &__job-level-select {
    width: 100%;
  }

  &__progress-line {
    margin: 0;
    line-height: 0;

    :deep(.ant-progress-outer) {
      display: block;
      width: 100%;
      padding-right: 0;
      margin-right: 0;
      line-height: 0;
    }

    :deep(.ant-progress-inner) {
      height: 6px;
      line-height: 0;
    }

    :deep(.ant-progress-bg) {
      height: 6px !important;
      background-color: #6BC85D;
    }
  }

  &__progress-text {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.65);
  }

  &__questions {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__question {
    display: flex;
    flex-direction: column;
  }

  :deep(.modal-header) {
    font-weight: 600;
  }

  &__body--complete {
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }
}
</style>
